/* RESPONSIBLE TEAM: team-standalone */
import Service, { inject as service } from '@ember/service';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import { tracked } from '@glimmer/tracking';
import { post, get } from 'embercom/lib/ajax';
import {
  IntegrationState,
  StandalonePlatform,
  StandalonePlatformDisplayName,
  IntegrationType,
} from 'embercom/objects/standalone/constants';
import type ApplicationInstance from '@ember/application/instance';
import { type TaskGenerator } from 'ember-concurrency';
import { action } from '@ember/object';
import { registerDestructor } from '@ember/destroyable';
import { assetUrl } from '@intercom/pulse/helpers/asset-url';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type { Channel } from 'embercom/components/standalone/deploy/overview/channel-options';
import { objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import {
  NEW_CONVERSATION_TARGET,
  NEW_FRESHDESK_TICKET_TARGET,
  NEW_SALESFORCE_CASE_TARGET,
  NEW_ZENDESK_TICKET_TARGET,
  NO_TRIGGER_TARGET,
} from 'embercom/lib/operator/custom-bots/constants';
import type IntlService from 'ember-intl/services/intl';
import type { BotConfigTarget } from 'embercom/objects/operator/configuration/configuration';
import type Store from '@ember-data/store';

export type IntegrationNavItems = Partial<Record<IntegrationType, IntegrationNavItem>>;
export type PlatformNavItems = Partial<Record<StandalonePlatform, IntegrationNavItems>>;
export type IntegrationStates = Partial<Record<IntegrationType, IntegrationState>>;

export interface IntegrationNavItem {
  route: string;
  urlSegments: Record<string, string>;
  labelIntlKey: string;
  activeRoutes?: string[];
  requiredFeatures?: string[];
  overviewItem?: Channel;
  isFinMessengerChannel?: boolean;
}

export interface IntegrationStatusResponse {
  integration_type: IntegrationType;
  integration_state: IntegrationState;
  // other fields omitted because they're unused
}

export interface FinMessengerStateResponse {
  state: IntegrationState;
}

export default class FinStandaloneIntegrationsService extends Service {
  @service declare store: Store;
  @service declare appService: { app: $TSFixMe };
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare intl: IntlService;

  @tracked integrationStates: IntegrationStates = {};

  platformTicketChannel({
    platform,
    target,
  }: {
    platform: StandalonePlatform;
    target: BotConfigTarget;
  }): Channel {
    let platformName = StandalonePlatformDisplayName[platform];
    return {
      icon: 'messenger-ticket-filled',
      title: this.intl.t('standalone.channels.overview.cards.generic-tickets.title', {
        platform: platformName,
      }),
      externalTypeName: 'ticket',
      integrationState: IntegrationState.NotConfigured,
      setupArgs: {
        description: this.intl.t('standalone.channels.overview.cards.generic-tickets.description', {
          platform: platformName,
        }),
        reactRoute: {
          route: 'apps.app.standalone.deploy.channel',
          urlSegments: {
            platform,
            channel: 'tickets',
          },
          routeParams: {
            tab: 'integrate',
          },
        },
        imageAssetUrl: assetUrl('/assets/images/standalone/zd-tickets.jpg'), // This will need to be updated with a Freshdesk image
        sectionTitle: this.intl.t(
          'standalone.deploy.overview.generic-platform.setup-ticket-channels',
          {
            platform: platformName,
          },
        ),
      },
      summaryArgs: {
        // TODO add statusPill: StandaloneIntercomMessengerPill,
        workflowRoute: 'apps.app.standalone.deploy.freshdesk.tickets.workflow.show',
        manageRouteReact: {
          route: 'apps.app.standalone.deploy.channel',
          urlSegments: {
            platform,
            channel: 'tickets',
          },
          routeParams: {
            tab: 'go-live',
          },
        },
        workflowParams: {
          target,
          objectType: objectTypes.triggerableCustomBot,
        },
      },
    };
  }

  intercomMessengerChannel({ platform }: { platform: StandalonePlatform }): Channel {
    return {
      icon: 'messenger',
      title: this.intl.t('standalone.channels.overview.cards.generic-messaging.title', {
        platform: StandalonePlatformDisplayName[platform],
      }),
      externalTypeName: 'conversation',
      integrationState: IntegrationState.NotConfigured,
      setupArgs: {
        description: this.intl.t(
          'standalone.channels.overview.cards.generic-messaging.description',
          {
            platform: StandalonePlatformDisplayName[platform],
          },
        ),
        reactRoute: {
          route: 'apps.app.standalone.deploy.channel',
          urlSegments: {
            platform,
            channel: 'messaging',
          },
          routeParams: {
            tab: 'integrate',
          },
        },
        imageAssetUrl: assetUrl('/assets/images/standalone/ic-messenger.jpg'),
        sectionTitle: this.intl.t(
          'standalone.deploy.overview.generic-platform.setup-chat-channels',
          {
            platform: StandalonePlatformDisplayName[platform],
          },
        ),
      },
      summaryArgs: {
        workflowRoute: 'apps.app.standalone.deploy.intercom-messenger.workflow.show',
        // TODO add statusPill: StandaloneIntercomMessengerPill,
        manageRouteReact: {
          route: 'apps.app.standalone.deploy.channel',
          urlSegments: {
            platform,
            channel: 'messaging',
          },
          routeParams: {
            tab: 'go-live',
          },
        },
        workflowParams: {
          target: NEW_CONVERSATION_TARGET,
          objectType: objectTypes.inboundCustomBot,
        },
      },
    };
  }

  get navItemsByPlatform(): PlatformNavItems {
    return {
      [StandalonePlatform.Salesforce]: {
        [IntegrationType.SalesforceCases]: {
          route: 'apps.app.standalone.deploy.channel',
          activeRoutes: ['apps.app.standalone.deploy.channel.salesforce.cases'],
          urlSegments: { platform: 'salesforce', channel: 'cases' },
          labelIntlKey: 'standalone.navbar.salesforce.cases',
          overviewItem: {
            ...this.platformTicketChannel({
              platform: StandalonePlatform.Salesforce,
              target: NEW_SALESFORCE_CASE_TARGET,
            }),
          },
        },
        [IntegrationType.IntercomMessenger]: {
          route: 'apps.app.standalone.deploy.channel',
          activeRoutes: ['apps.app.standalone.deploy.channel.salesforce.messaging'],
          urlSegments: { platform: 'salesforce', channel: 'messaging' },
          labelIntlKey: 'standalone.navbar.salesforce.messaging',
          isFinMessengerChannel: true,
          overviewItem: {
            ...this.intercomMessengerChannel({ platform: StandalonePlatform.Salesforce }),
          },
        },
      },
      [StandalonePlatform.Freshdesk]: {
        [IntegrationType.FreshdeskTickets]: {
          route: 'apps.app.standalone.deploy.channel',
          activeRoutes: ['apps.app.standalone.deploy.channel.freshdesk.tickets'],
          urlSegments: { platform: 'freshdesk', channel: 'tickets' },
          labelIntlKey: 'standalone.navbar.freshdesk.tickets',
          overviewItem: {
            ...this.platformTicketChannel({
              platform: StandalonePlatform.Freshdesk,
              target: NEW_FRESHDESK_TICKET_TARGET,
            }),
          },
        },
        [IntegrationType.Freshchat]: {
          route: 'apps.app.standalone.deploy.channel',
          activeRoutes: ['apps.app.standalone.deploy.channel.freshdesk.messaging'],
          urlSegments: { platform: 'freshdesk', channel: 'messaging' },
          labelIntlKey: 'standalone.navbar.freshdesk.messaging',
          isFinMessengerChannel: true,
          overviewItem: {
            ...this.intercomMessengerChannel({ platform: StandalonePlatform.Freshdesk }),
          },
        },
      },
      [StandalonePlatform.Hubspot]: {
        [IntegrationType.HubspotTickets]: {
          route: 'apps.app.standalone.deploy.channel',
          activeRoutes: ['apps.app.standalone.deploy.channel.hubspot.tickets'],
          urlSegments: { platform: 'hubspot', channel: 'tickets' },
          labelIntlKey: 'standalone.navbar.hubspot.tickets',
          overviewItem: {
            ...this.platformTicketChannel({
              platform: StandalonePlatform.Hubspot,
              target: NO_TRIGGER_TARGET,
            }),
          },
        },
        [IntegrationType.HubspotMessenger]: {
          route: 'apps.app.standalone.deploy.channel',
          activeRoutes: ['apps.app.standalone.deploy.channel.hubspot.messaging'],
          urlSegments: { platform: 'hubspot', channel: 'messaging' },
          labelIntlKey: 'standalone.navbar.hubspot.messaging',
          overviewItem: {
            ...this.intercomMessengerChannel({ platform: StandalonePlatform.Hubspot }),
          },
        },
      },
      [StandalonePlatform.CustomHelpdesk]: {
        [IntegrationType.CustomHelpdesk]: {
          route: 'apps.app.standalone.deploy.channel',
          activeRoutes: ['apps.app.standalone.deploy.channel.custom_helpdesk.messaging'],
          urlSegments: { platform: 'custom_helpdesk', channel: 'messaging' },
          labelIntlKey: 'standalone.navbar.custom_helpdesk.messaging',
          overviewItem: {
            ...this.intercomMessengerChannel({ platform: StandalonePlatform.CustomHelpdesk }),
          },
        },
      },
      [StandalonePlatform.ZendeskLite]: {
        [IntegrationType.ZendeskLiteTickets]: {
          route: 'apps.app.standalone.deploy.channel',
          activeRoutes: ['apps.app.standalone.deploy.channel.zendesk-lite.tickets'],
          urlSegments: { platform: 'zendesk_lite', channel: 'tickets' },
          labelIntlKey: 'standalone.navbar.zendesk-lite.tickets',
          overviewItem: {
            ...this.platformTicketChannel({
              platform: StandalonePlatform.ZendeskLite,
              target: NEW_ZENDESK_TICKET_TARGET,
            }),
          },
        },
        [IntegrationType.ZendeskLiteMessenger]: {
          route: 'apps.app.standalone.deploy.channel',
          activeRoutes: ['apps.app.standalone.deploy.channel.zendesk-lite.messaging'],
          urlSegments: { platform: 'zendesk_lite', channel: 'messaging' },
          labelIntlKey: 'standalone.navbar.zendesk-lite.messaging',
          overviewItem: {
            ...this.intercomMessengerChannel({ platform: StandalonePlatform.ZendeskLite }),
          },
        },
      },
      // not implemented until integration has been added in backend
      // [StandalonePlatform.Zendesk],
      // [StandalonePlatform.Salesforce],
      // [StandalonePlatform.Gorgias],
      // [StandalonePlatform.ZohoDesk],
      // [StandalonePlatform.HelpScout],
      // [StandalonePlatform.ServiceNow],
      // [StandalonePlatform.AmazonConnect],
      // [StandalonePlatform.Front],
    };
  }

  constructor(owner: ApplicationInstance) {
    super(owner);
    taskFor(this.loadIntegrationStatesTask).perform();
    // Subscribe to standalone-deploy-integrations-updated event from react deploy area
    let unsubscribe = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'standalone-deploy-integrations-updated',
      () => {
        taskFor(this.loadIntegrationStatesTask).perform();
        taskFor(this.loadStandaloneIntegrations).perform();
      },
    );

    // Register destructor to clean up subscription
    registerDestructor(this, unsubscribe);
  }

  @action
  getIsLiveFn(integrationType: IntegrationType): () => boolean {
    return () => this.integrationStates[integrationType] === IntegrationState.Enabled;
  }

  get platform(): StandalonePlatform {
    return this.appService.app.standalone_platforms?.[0].platform_type;
  }

  get platformIntegrationTypes(): IntegrationType[] {
    let items = this.navItemsByPlatform[this.platform] ?? {};

    return Object.entries(items)
      .filter(([, value]) => value !== undefined)
      .map(([key]) => key as unknown as IntegrationType);
  }

  get navItems() {
    let itemsForPlatform = this.navItemsByPlatform[this.platform];
    if (!itemsForPlatform) {
      return [];
    }

    return this.platformIntegrationTypes
      .map((integrationType) => {
        let baseItem = itemsForPlatform?.[integrationType];
        if (!baseItem) {
          return undefined;
        }
        delete baseItem.overviewItem;
        return {
          ...baseItem,
          isLive: this.getIsLiveFn(integrationType),
        };
      })
      .filter(Boolean);
  }

  get overviewItems() {
    let itemsForPlatform = this.navItemsByPlatform[this.platform];
    if (!itemsForPlatform) {
      return [];
    }

    return this.platformIntegrationTypes
      .map((integrationType) => {
        let baseItem = itemsForPlatform?.[integrationType];
        if (!baseItem) {
          return undefined;
        }
        return {
          ...baseItem.overviewItem,
          integrationState:
            this.integrationStates[integrationType] ?? IntegrationState.NotConfigured,
        };
      })
      .filter((item): item is Channel => Boolean(item));
  }

  *loadFinMessengerStates(updatedStates: IntegrationStates): TaskGenerator<void> {
    let itemsForPlatform = this.navItemsByPlatform[this.platform];
    if (!itemsForPlatform) {
      return;
    }

    for (let integrationType of this.platformIntegrationTypes) {
      let navItem = itemsForPlatform[integrationType];
      if (navItem?.isFinMessengerChannel) {
        let finMessengerResponse: FinMessengerStateResponse = yield get(
          `/ember/standalone/intercom_messenger_configurations/${this.appService.app.id}/state`,
        );
        updatedStates[integrationType] =
          finMessengerResponse.state ?? IntegrationState.NotConfigured;
      }
    }
  }

  @dropTask *loadStandaloneIntegrations(): TaskGenerator<void> {
    // reset the strore before pulling integration again.
    // This is called from an event sent from the react deploy area when the integrations state chaanges.
    // If we get no integrations returned from the backend we need to remove the integrations from the store.
    this.store.unloadAll('standalone/integration');
    yield this.store.findAll('standalone/integration');
  }

  @restartableTask *loadIntegrationStatesTask(): TaskGenerator<void> {
    let response: IntegrationStatusResponse[] = yield post('/ember/standalone/integrations', {
      app_id: this.appService.app.id,
      integration_types: this.platformIntegrationTypes,
    });

    let updatedStates: IntegrationStates = {};
    for (let { integration_type, integration_state } of response) {
      updatedStates[integration_type] = integration_state;
    }

    // Override states for Fin Messenger channels
    yield* this.loadFinMessengerStates(updatedStates);

    this.integrationStates = updatedStates;
  }
}
